import { Box, ButtonBase, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { Plus } from 'lucide-react';
import { FormattedMessage } from 'react-intl';
import LinkTile, { LINK_TILE_PX } from '~/components/LinkTile';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import ReviewStatus from '~/components/Structures/ReviewStatus';
import RegistrationInfoButton from './RegistrationInfoButton';
import { registrationKindSymbol } from './registrationKind';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import type { RegistrationKind } from '~/domain/patient/approvableRegistration';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { ICON_PX } from '~/theme/iconSizes';

/** A row's layout, with the hover and focus fill of one that takes a click. */
const rowSx = (selected: boolean, takesClick: boolean) => (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 1,
    width: '100%',
    minHeight: 28,
    py: 0.5,
    // With the list's 4 px, the lead stands 8 px in.
    pl: 0.5,
    pr: 1,
    borderRadius: `${theme.shape.borderRadius}px`,
    textAlign: 'left',
    font: 'inherit',
    color: 'text.primary',
    backgroundColor: selected ? theme.palette.action.selected : 'transparent',
    ...(takesClick && {
        transition: theme.transitions.create('background-color', {
            duration: theme.transitions.duration.short,
        }),
        '&:hover': {
            backgroundColor: selected
                ? theme.palette.action.selected
                : theme.palette.action.hover,
        },
        '&.Mui-focusVisible': {
            backgroundColor: theme.palette.action.hover,
        },
    }),
});

/**
 * A registration in a list: its link mark, its label, which is PHI and
 * masked, its approval status and, with `details`, the (i) to its details.
 * The selected one, the registration the pair is displayed through, is
 * filled. A row with `onClick` activates the registration; one without it
 * takes no click. The rows sit in a list padded 4 px.
 */
export function RegistrationRow({
    label,
    kind,
    approvalStatus,
    selected,
    onClick,
    details,
}: {
    label: string;
    kind?: RegistrationKind;
    approvalStatus: DICOMApprovalStatus;
    selected: boolean;
    onClick?: () => void;
    /** The registration the (i) opens, and its delete when it may go. */
    details?: { registration: RigidRegistration; onDelete?: () => void };
}) {
    const content = (
        <>
            <LinkTile selected={selected} />
            {kind && (
                <Typography
                    component="span"
                    data-testid="registration-kind-symbol"
                    fontSize={10}
                    fontWeight={700}
                    lineHeight={1}
                    color={selected ? 'primary.main' : 'text.secondary'}
                    sx={{
                        flexShrink: 0,
                        px: 0.5,
                        py: 0.25,
                        border: '1px solid',
                        borderColor: selected ? 'primary.main' : 'divider',
                        borderRadius: 1,
                    }}
                >
                    {registrationKindSymbol(kind)}
                </Typography>
            )}
            <Typography
                component="span"
                fontSize={12}
                color={selected ? 'text.primary' : 'text.secondary'}
                noWrap
                flex={1}
                minWidth={0}
            >
                <SensitiveInfoTypography>{label}</SensitiveInfoTypography>
            </Typography>
            <Box flexShrink={0} whiteSpace="nowrap">
                <ReviewStatus
                    status={approvalStatus}
                    fontSize={11}
                    color="text.secondary"
                />
            </Box>
            {details && (
                <RegistrationInfoButton
                    name={label}
                    registration={details.registration}
                    onDelete={details.onDelete}
                />
            )}
        </>
    );
    const rowProps = {
        'aria-current': selected || undefined,
        'data-testid': 'registration-item',
        'data-selected': selected,
    };
    return onClick ? (
        <ButtonBase
            component="div"
            onClick={onClick}
            disableRipple
            {...rowProps}
            sx={rowSx(selected, true)}
        >
            {content}
        </ButtonBase>
    ) : (
        <Box {...rowProps} sx={rowSx(selected, false)}>
            {content}
        </Box>
    );
}

/** The last row under an image: creates a registration to the primary image. */
export function NewRegistrationRow({ onClick }: { onClick: () => void }) {
    return (
        <ButtonBase
            onClick={onClick}
            disableRipple
            data-testid="create-registration-button"
            sx={rowSx(false, true)}
        >
            <Box
                component="span"
                display="flex"
                justifyContent="center"
                width={LINK_TILE_PX}
                color="primary.main"
            >
                <Plus size={ICON_PX} />
            </Box>
            <Typography component="span" fontSize={12} color="text.primary">
                <FormattedMessage id="workflow.imageRegistration.newRegistration" />
            </Typography>
        </ButtonBase>
    );
}
